import { css } from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';
import { getFontScale } from 'themes/mixins/getFontScale';

const motives = {
  default: css`
    font-family: 'LFT Zeno', sans-serif;

    th {
      padding: 1.9rem 0;

      border-top: 0.2rem solid ${pickColor('borderLight')};
      border-bottom: 0.2rem solid ${pickColor('borderLight')};

      font-weight: 600;
    }

    th:first-of-type {
      padding-left: 2rem;
    }

    tbody tr td:first-child {
      padding-left: 2rem;
    }

    td {
      padding: 0.8rem 0;

      vertical-align: text-top;
    }

    tr {
      ${getFontScale(['base', 's'])};
    }
  `,
  secondary: css`
    font-family: 'LFT Zeno', sans-serif;

    th {
      padding-bottom: 1.9rem;

      border-bottom: 0.2rem solid ${pickColor('borderTransparent')};

      color: ${pickColor('white')};

      ${getFontScale('s')};
    }

    td {
      padding: 0.8rem;

      text-transform: uppercase;

      vertical-align: text-top;

      border-bottom: 0.1rem solid ${pickColor('borderTransparent')};
      color: ${pickColor('white')};
    }

    tr {
      background-color: ${pickColor('black')};
    }
  `,
  tertiary: css`
    font-family: 'LFT Zeno', sans-serif;
    font-weight: lighter;

    th {
      padding-bottom: 1.9rem;

      border-bottom: 0.2rem solid ${pickColor('borderExtraLight')};

      text-transform: capitalize;

      color: ${pickColor('grayDark')};
      ${getFontScale('s')};
    }

    td {
      padding: 0.8rem;

      text-transform: uppercase;

      vertical-align: text-top;

      border-bottom: 0.1rem solid ${pickColor('borderExtraLight')};
      color: ${pickColor('grayDark')};

      font-weight: 300;
      font-family: 'LFT Zeno', sans-serif;

      ${getFontScale('xs')};
    }

    tr {
      background-color: inherit;
    }
  `,
  quaternary: css`
    th {
      padding-bottom: 1.2rem;

      font-weight: 700;
      font-family: 'LFT Zeno Condensed', sans-serif;

      letter-spacing: 0.1rem;

      color: ${pickColor('grayDark')};
      ${getFontScale('xs')};
    }

    td {
      padding: 0.8rem;

      text-transform: uppercase;
      font-weight: 400;
      font-family: 'LFT Zeno', sans-serif;

      ${getFontScale('xs')};

      vertical-align: text-top;

      color: ${pickColor('grayDark')};
    }

    tr {
      background-color: inherit;
    }
  `,
  quinary: css`
    tr {
      height: 5rem;
    }

    th {
      padding: 0.2rem;

      border: 0.1rem solid ${pickColor('borderLight')};

      ${getFontScale('xs')};

      &:not(:last-of-type) {
        vertical-align: bottom;
      }
    }

    tbody {
      td {
        border: 0.1rem solid ${pickColor('borderLight')};

        ${getFontScale('xs')};
      }
    }
  `,
  senary: css`
    font-family: 'LFT Zeno', sans-serif;
    font-weight: 300;
    ${getFontScale('s')};

    table-layout: fixed;

    th {
      color: ${pickColor('grayDark')};
      border-bottom: 0.2rem solid ${pickColor('borderExtraLight')};

      &#header_num {
        width: 6%;
      }

      &#header_name {
        width: 46%;
      }

      &#header_date {
        width: 40%;
      }

      &#header_id {
        width: 8%;
        text-align: center;
      }
    }

    tbody {
      tr:nth-child(odd) {
        background-color: ${pickColor('blackTransparentTertiary')};
      }

      td {
        color: ${pickColor('grayDark')};
        border-bottom: 0.1rem solid ${pickColor('borderExtraLight')};
      }
    }
  `,
  budget: css`
    font-family: 'LFT Zeno', sans-serif;
    font-weight: lighter;

    table-layout: fixed;

    th {
      width: 12%;

      padding: 0.8rem 0;
      padding-bottom: 1.9rem;

      border-bottom: 0.2rem solid ${pickColor('borderExtraLight')};
      text-wrap: wrap;
      vertical-align: baseline;

      color: ${pickColor('grayDark')};
      ${getFontScale('s')};

      &#header_marketing {
        width: 15%;
      }

      &#header_styleCodeAndStyleDesc {
        width: 30%;
      }

      &#header_currQuantity {
        width: 10%;
      }
    }

    td {
      padding: 0.8rem 0.2rem;

      text-transform: uppercase;
      text-wrap: wrap;

      vertical-align: text-top;

      color: ${pickColor('grayDark')};

      font-weight: 300;
      font-family: 'LFT Zeno', sans-serif;

      text-align: left;

      ${getFontScale('s')};
    }

    tr {
      background-color: inherit;
    }
  `,
  budgetSecondary: css`
    font-family: 'LFT Zeno', sans-serif;
    font-weight: lighter;

    table-layout: fixed;

    th {
      width: 10%;
      text-wrap: wrap;
      vertical-align: baseline;

      padding: 0.8rem 0;
      padding-bottom: 1.9rem;

      border-bottom: 0.2rem solid ${pickColor('borderExtraLight')};

      color: ${pickColor('grayDark')};
      ${getFontScale('xs')};

      &#header_styleCodeAndStyleDesc {
        width: 30%;
      }

      &#header_mainFabricDesc {
        width: 11%;
      }

      &#header_currQuantity {
        width: 8%;
      }

      &#header_prevSold {
        width: 8%;
      }
    }

    td {
      padding: 0.8rem 0;

      text-transform: uppercase;

      vertical-align: text-top;

      color: ${pickColor('grayDark')};

      font-weight: 300;
      font-family: 'LFT Zeno', sans-serif;

      text-align: left;

      ${getFontScale('xs')};
    }

    tr {
      background-color: inherit;
    }
  `,
};

export type TableMotives = keyof typeof motives;

const pickMotive = (motive: TableMotives) => motives[motive];

export { pickMotive };
